import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { dispatch, RootState } from '../../store/store';
import moment from 'moment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ClassicSBOddsBox from './ClassicSBOddsBox';

import './ClassicSBEvents.scss';
import { setSBEvents, setSBSelectedSport } from '../../store/exchangeSports/exchangeSportsReducer';
import { fetchSports, listLiveSBEvents, listSBEvents } from '../../store/exchangeSports/exchangeSportsAction';
import Loader from "../../assets/images/Strike_White_Logo_Transparent_GIF.gif";
import Inplay from '../../assets/sports/inplay.gif'
import { sbsportsIconMap, srsportNamesMap } from '../../constants/sports';
import { useNavigate } from 'react-router';
import { TopicUrls } from '../../models/EventData';
import { checkStompClientSubscriptions, disconnectToWS, subscribeSportRadarEventOdds } from '../../websockets/webSocket';
import EventPagination from '../EventPagination/EventPagination';

interface StoreProps {
    sports: any[];
    sbSelectedSport: string;
    setSBSelectedSport: (data: string) => void;
    listSBEvents: (inplay: boolean, sportId: string, pageNo: number) => void;
    sbEvents: Record<string, Record<string, any>>;
    listLiveSBEvents: () => void;
    topicUrls?: TopicUrls;
    sportsRadarWSConnected: boolean;
}

const ClassicSBEvents: React.FC<StoreProps> = (props) => {
    const { sports, sbSelectedSport, setSBSelectedSport, listSBEvents, sbEvents, listLiveSBEvents, topicUrls, sportsRadarWSConnected } = props;
    const LIVE = "LIVE";
    const customOrder = ["sr:sport:21", "sr:sport:1", "sr:sport:5"];
    const customGroupOrder = ["LIVE", "LIVE-sr:sport:21", "LIVE-sr:sport:1", "LIVE-sr:sport:5", "UPCOMING"];
    const [sortedEvents, setSortedEvents] = useState<any>();
    const [sortedSports, setSortedSports] = useState<any>();
    const [loading, setLoading] = useState<boolean>(false);
    const [expandedSports, setExpandedSports] = useState<Record<string, boolean>>({});
    const navigate = useNavigate();
    const [pageNumbers, setPageNumbers] = useState<Record<string, number>>({});

    useEffect(() => {
        const init = async () => {
            if (sports && sports.length > 0) {
                const sortedSports = [...sports].sort((a, b) => {
                    const indexA = customOrder.indexOf(a.sportId?.toString());
                    const indexB = customOrder.indexOf(b.sportId?.toString());

                    if (indexA === -1 && indexB === -1) return 0;
                    if (indexA === -1) return 1;
                    if (indexB === -1) return -1;

                    return indexA - indexB;
                });
                setSortedSports(sortedSports);
                if (!sbSelectedSport) {
                    setSBSelectedSport(LIVE);
                }
            } else {
                setLoading(true);
                await dispatch(fetchSports("SB123"));
                setLoading(false);
            }
        };
        init();

    }, [sports]);

    useEffect(() => {
        setExpandedSports({});
        if (sbSelectedSport === LIVE) {
            listLiveSBEvents();
        } else if (sbSelectedSport) {
            dispatch(setSBEvents({}));
            const livePage = 1;
            const upcomingPage = 1;
            listSBEvents(true, sbSelectedSport, livePage);
            listSBEvents(false, sbSelectedSport, upcomingPage);
        }
        setPageNumbers(prev => ({
            ...prev,
            [`LIVE-${sbSelectedSport}`]: 1,
            'LIVE': 1,
            'UPCOMING': 1
        }));
    }, [sbSelectedSport]);

    useEffect(() => {
        if (topicUrls?.premiumBaseUrl) {
            const baseUrlsPayload = {
                matchOddsBaseUrl: null,
                bookMakerAndFancyBaseUrl: null,
                premiumBaseUrl: topicUrls?.premiumBaseUrl,
            };
            checkStompClientSubscriptions(baseUrlsPayload);
        }
    }, [topicUrls]);

    useEffect(() => {
        return () => {
            disconnectToWS();
        };
    }, []);

    useEffect(() => {
        if (!sportsRadarWSConnected || !topicUrls?.premiumTopic) return;

        const newSubscriptions = new Set();
        Object.entries(sbEvents || {}).forEach(([sportKey, eventMap]) => {
            Object.values(eventMap || {}).forEach((event: any) => {
                const marketName = event?.markets?.matchOdds?.[0]?.marketName
                const shouldSubscribe =
                    event?.status === 'IN_PLAY' &&
                    event?.providerName === 'SportRadar' &&
                    !newSubscriptions.has(event.eventId) &&
                    event?.markets?.matchOdds?.length > 0 &&
                    marketName &&
                    (marketName?.toLowerCase().startsWith('winner') || marketName === "1x2");

                if (shouldSubscribe) {
                    newSubscriptions.add(event.eventId);
                    subscribeSportRadarEventOdds(
                        topicUrls?.premiumTopic,
                        sportKey,
                        event?.eventId,
                    );
                }
            });
        });
    }, [sbEvents, sportsRadarWSConnected]);

    useEffect(() => {
        const validEntries = Object.entries(sbEvents).filter(
            ([, eventMap]) => eventMap && typeof eventMap === 'object' && Object.keys(eventMap).length > 0
        );

        const sortedEntries = validEntries.sort(([keyA], [keyB]) => {
            const indexA = customGroupOrder.indexOf(keyA);
            const indexB = customGroupOrder.indexOf(keyB);

            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;

            return keyA.localeCompare(keyB);
        });
        setSortedEvents(sortedEntries);
    }, [sbEvents]);

    const toggleSport = (sportId: string) => {
        setExpandedSports(prev => ({
            ...prev,
            [sportId]: !prev[sportId]
        }));
    };

    const handleEventClick = (eventDto: any) => {
        navigate(`/sb-markets/${btoa(eventDto?.sportId + "|" + eventDto?.competitionId + "|" + eventDto?.eventId)}`);
    };

    const renderMatchOdds = (event: any) => {
        const matchOddsMarket = event?.markets?.matchOdds?.find((market: any) =>
            (market?.marketName.toLowerCase().startsWith('winner') || market?.marketName === "1x2") && market.runners.length <= 3
        );

        if (!matchOddsMarket) {
            return (
                <div className="match-odds">
                    <div className="odd-box">-</div>
                    <div className="odd-box">-</div>
                </div>
            );
        }

        const sortedRunners = [...matchOddsMarket.runners];
        const drawIndex = sortedRunners.findIndex(runner =>
            runner?.runnerName?.toLowerCase().includes('draw')
        );

        if (drawIndex !== -1 && sortedRunners.length === 3) {
            const draw = sortedRunners.splice(drawIndex, 1)[0];
            sortedRunners.splice(1, 0, draw);
        }

        const isSuspendedOrClosed =
            event?.status?.toUpperCase() === 'SUSPENDED' ||
            event?.status?.toUpperCase() === 'CLOSED' ||
            matchOddsMarket?.status?.toUpperCase() !== "ACTIVE";

        return (
            <div className="match-odds">
                {sortedRunners.map((runner: any, index: number) => {
                    const price = runner?.backPrices?.[0]?.price;
                    const runnerId = `${event.eventId}-${runner.runnerId}`;

                    return (
                        <ClassicSBOddsBox
                            key={runnerId}
                            price={price}
                            runnerId={runnerId}
                            isSuspendedOrClosed={isSuspendedOrClosed || runner?.status?.toUpperCase() !== "ACTIVE"}
                        />
                    );
                })}
            </div>
        );
    };

    const renderEvents = () => {
        if (!sortedEvents?.length) {
            return (
                <div className="no-events">
                    <span>No Events</span>
                </div>
            );
        }

        return (sortedEvents ?? []).map(([sportId, events]: [string, any]) => {
            // events is an object with eventId keys and possibly totalCount
            const eventList = Object.values(events || {}).filter((event: any) => typeof event === 'object' && event !== null && event.eventId);
            const totalCount = events.totalCount || 0;
            const currentPage = pageNumbers[sportId] || 1;
            const canShowMore = totalCount > eventList.length;


            return (
                <div key={sportId} className="sport-container">
                    <div
                        className={`sport-header ${expandedSports[sportId] ? '' : 'expanded'}`}
                        onClick={() => toggleSport(sportId)}
                    >
                        <div className="sport-name">
                            <img src={sbsportsIconMap[sportId?.split("-")?.[1] || sportId]} className={sbsportsIconMap[sportId?.split("-")?.[1] || sportId] ? "sport-icon" : undefined} />
                            {srsportNamesMap[sportId.includes("-") ? sportId.split("-")[1] : sportId]}
                        </div>
                        <div className="header-right">
                            {/* <div className="event-count">{events.length}</div> */}
                            {expandedSports[sportId] ? (
                                <ExpandLessIcon className="expand-icon" />
                            ) : (
                                <ExpandMoreIcon className="expand-icon" />
                            )}
                        </div>
                    </div>
                    <div className={`sport-events ${expandedSports[sportId] ? '' : 'expanded'}`}>
                        {eventList.map((event: any) => (
                            <div key={event.eventId} className="event-row" onClick={() =>
                                handleEventClick(event)
                            }>
                                <div className="event-info">
                                    <div className="competition-name" title={event?.competitionName}>
                                        {event?.competitionName}
                                    </div>
                                    <div className="event-time">
                                        <span className="event-date">
                                            {moment(event.openDate).isSame(moment(), 'day')
                                                ? `Today - ${moment(event.openDate).format('HH:mm')}`
                                                : moment(event.openDate).format('MMM DD - HH:mm')}
                                        </span>

                                        {event?.status === "IN_PLAY" && <span className="live-indicator">LIVE</span>}
                                    </div>
                                    <div className="event-teams">
                                        {event?.eventName?.split(' vs. ')?.map((team: string, index: number) => (
                                            <div key={index} className="team-name" title={team}>
                                                <img src={sbsportsIconMap[event?.sportId]} className="team-icon" />
                                                <span>{team}</span>
                                                {event.status === 'IN_PLAY' && (
                                                    <span className="score">
                                                        {index === 0 ? event.homeScore : event.awayScore}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {renderMatchOdds(event)}
                            </div>
                        ))}
                        {canShowMore && sbSelectedSport !== LIVE && (
                            <EventPagination
                                currentPage={currentPage}
                                eventsCount={totalCount}
                                setCurrentPage={(page: number) => {
                                    setPageNumbers(prev => ({ ...prev, [sportId]: page }));
                                    if (sportId.startsWith('LIVE')) {
                                        listSBEvents(true, sbSelectedSport, page);
                                    } else if (sportId === 'UPCOMING') {
                                        listSBEvents(false, sbSelectedSport, page);
                                    }
                                }}
                            />
                        )}
                    </div>
                </div>
            );
        });
    };

    return (
        <>
            {loading ? (
                <div className="home-loading">
                    <img src={Loader} style={{ height: '10rem' }} />
                </div>
            ) : (
                <>
                    <div className="sb-sports-tabs-ctn">
                        <div className="sb-sports-tabs">
                            <div className="sb-sport-tabs">
                                <button
                                    className={`sb-tab ${sbSelectedSport === LIVE ? 'sb-selected-tab' : ''}`}
                                    onClick={() => setSBSelectedSport(LIVE)}
                                >
                                    <img src={Inplay} className="sb-sub-header-icons" style={{ margin: "-6px 0px 0px 4px", height: "25px", width: "50px" }} />
                                </button>
                                {sortedSports?.map((sport: any) => (
                                    <button
                                        key={sport?.sportId}
                                        className={`sb-tab ${sbSelectedSport === sport?.sportId ? 'sb-selected-tab' : ''}`}
                                        onClick={() => setSBSelectedSport(sport?.sportId)}
                                    >
                                        <img src={sbsportsIconMap[sport?.sportId]} className="sb-sub-header-icons icon-color" />
                                        {sport.sportName}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="events-container">
                        {renderEvents()}
                    </div>
                </>
            )}
        </>
    );
};

const mapStateToProps = (state: RootState) => {
    return {
        sports: state.exchangeSports.sbSports,
        sbSelectedSport: state.exchangeSports.sbSelectedSport,
        sbEvents: state.exchangeSports.sbEvents,
        topicUrls: state?.exchangeSports?.topicUrls,
        sportsRadarWSConnected: state?.exchangeSports?.sportsRadarWSConnected,
    };
};

const mapDispatchToProps = (dispatch: Function) => {
    return {
        setSBSelectedSport: (data: string) => dispatch(setSBSelectedSport(data)),
        listSBEvents: (inplay: boolean, sportId: string, pageNo: number) => dispatch(listSBEvents(inplay, sportId, pageNo)),
        listLiveSBEvents: () => dispatch(listLiveSBEvents()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ClassicSBEvents);
